import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import type { DiffItem } from "../VersionDiffViewer";
import { buildVersionReviewItems } from "../utils/review-items";

import { DiffContent } from "./DiffContent";

import BreadcrumbsPageWrapper from "@/components/UnifiedRegistry/BreadcrumbsPageWrapper";
import useFolderPathBreadcrumbs from "@/components/UnifiedRegistry/hooks/useFolderPathBreadcrumbs";
import { useGetSkillCollection, useGetSkillCollectionVersion } from "@/queries/skill-collections";

const SkillCollectionDiffPage = () => {
  const { workspaceId, collectionId, versionNumber, compareVersionNumber } = useParams();
  const workspaceIdNum = workspaceId ? parseInt(workspaceId, 10) : null;
  const collectionIdStr = collectionId ?? null;
  const urlVersionA = versionNumber ? parseInt(versionNumber, 10) : null;
  const urlVersionB = compareVersionNumber ? parseInt(compareVersionNumber, 10) : null;
  const fromVersion =
    urlVersionA !== null && urlVersionB !== null
      ? Math.min(urlVersionA, urlVersionB)
      : (urlVersionA ?? urlVersionB);
  const toVersion =
    urlVersionA !== null && urlVersionB !== null
      ? Math.max(urlVersionA, urlVersionB)
      : (urlVersionA ?? urlVersionB);

  const { data: collectionData } = useGetSkillCollection(workspaceIdNum, collectionIdStr);
  const collectionName = collectionData?.skill_collection?.name;
  const baseBreadcrumbs = useFolderPathBreadcrumbs(null, collectionName);
  const collectionLink = `/workspace/${workspaceId}/skill-collection/${collectionId}/version/${versionNumber}`;
  const breadcrumbProps = {
    ...baseBreadcrumbs,
    items: collectionName
      ? [
          ...baseBreadcrumbs.items.slice(0, -1),
          { label: collectionName, id: null, href: collectionLink },
          { label: "Diff View", id: null },
        ]
      : baseBreadcrumbs.items,
  };

  const { data: olderData, isLoading: isLoadingOlder } = useGetSkillCollectionVersion(
    workspaceIdNum,
    collectionIdStr,
    fromVersion,
  );
  const { data: newerData, isLoading: isLoadingNewer } = useGetSkillCollectionVersion(
    workspaceIdNum,
    collectionIdStr,
    toVersion,
  );

  const isLoading = isLoadingOlder || isLoadingNewer;

  const diffItems = useMemo(() => {
    if (!olderData?.files || !newerData?.files) {
      return [];
    }
    return buildVersionReviewItems({
      previousFiles: olderData.files,
      nextFiles: newerData.files,
      previousProvider: olderData.version.provider,
      nextProvider: newerData.version.provider,
      previousFilePaths: olderData.version.file_paths,
      nextFilePaths: newerData.version.file_paths,
    }) as DiffItem[];
  }, [
    olderData?.files,
    olderData?.version.provider,
    olderData?.version.file_paths,
    newerData?.files,
    newerData?.version.provider,
    newerData?.version.file_paths,
  ]);

  return (
    <BreadcrumbsPageWrapper {...breadcrumbProps} className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between pb-3">
        <div className="flex items-center gap-3">
          <Link
            to={`/workspace/${workspaceId}/skill-collection/${collectionId}/version/${fromVersion}`}
            className="text-sm font-semibold hover:text-blue-600"
          >
            Version {fromVersion}
          </Link>
          <span className="text-sm text-gray-400">vs</span>
          <Link
            to={`/workspace/${workspaceId}/skill-collection/${collectionId}/version/${toVersion}`}
            className="text-sm font-semibold hover:text-blue-600"
          >
            Version {toVersion}
          </Link>
        </div>
        <p className="text-sm font-medium text-gray-900">
          {diffItems.length} change{diffItems.length === 1 ? "" : "s"}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        <DiffContent isLoading={isLoading} diffItems={diffItems} />
      </div>
    </BreadcrumbsPageWrapper>
  );
};

export default SkillCollectionDiffPage;
